import { useCallback, useState } from 'react';
import {
  Form,
  FormSizes,
  Checkbox,
  Button,
  ButtonVariants,
  ButtonSizes,
  Alert,
  AlertVariants,
  AlertAppearances,
  AlertOrientations,
  Typography,
  TypographyVariants,
  TypographyElements,
  FormValidateOn,
} from '@carboledger/ui';
import { PasswordRequirementChecklist } from './PasswordRequirementChecklist';
import { NewPasswordField, ConfirmPasswordField } from './PasswordFields';
import styles from './ResetPasswordForm.module.scss';
import { useTranslations } from '@/hooks';

/**
 * Props:
 *   onSubmit        ({ password, confirmPassword }) => void
 *   onBackToLogin   () => void
 *   isLoading       true while the resetPassword call is in flight
 *   error           RTK Query error from that call, or undefined
 */
export const ResetPasswordForm = ({ onSubmit, onBackToLogin, isLoading, error }) => {
  const t = useTranslations();
  const [showPassword, setShowPassword] = useState(false);

  const handleShowPassword = useCallback((event) => {
    setShowPassword(event.target.checked);
  }, []);

  return (
    <Form
      validateOn={FormValidateOn.change}
      initialValues={{ password: '', confirmPassword: '' }}
      onSubmit={onSubmit}
      size={FormSizes.small}
      className={styles.root}
    >
      {(form) => (
        <>
          <Typography
            variant={TypographyVariants.h6}
            as={TypographyElements.h1}
            className={styles.heading}
          >
            {t('AUTH_RESET_TITLE')}
          </Typography>

          {error && (
            <Alert
              variant={AlertVariants.danger}
              appearance={AlertAppearances.accent}
              orientation={AlertOrientations.horizontal}
              className={styles.alert}
            >
              {error.data?.message ?? t('ERROR_GENERIC')}
            </Alert>
          )}

          <div className={styles.fields}>
            <div className={styles.passwordField}>
              <NewPasswordField showPassword={showPassword} />
              <Checkbox
                label={t('AUTH_SHOW_PASSWORD')}
                checked={showPassword}
                onChange={handleShowPassword}
              />
            </div>

            <div className={styles.passwordField}>
              <ConfirmPasswordField showPassword={showPassword} />
              <PasswordRequirementChecklist password={form.values.password} />
            </div>
          </div>

          <div className={styles.actions}>
            <Button type="submit" fullWidth size={ButtonSizes.sm} loading={isLoading}>
              {t('AUTH_SET_PASSWORD_ACTION')}
            </Button>
            <Button
              type="button"
              variant={ButtonVariants.ghost}
              fullWidth
              size={ButtonSizes.sm}
              onClick={onBackToLogin}
            >
              {t('AUTH_BACK_TO_LOGIN')}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};
